import type { Factor, User } from "@supabase/supabase-js"
import { initPromise } from "@/hooks/auth/use-auth"
import { ALLOWED_EMAIL_DOMAIN } from "@/lib/constants"
import { supabase } from "@/lib/supabase"

export const AuthLevel = {
	PasswordOnly: "aal1",
	FullyAuthenticated: "aal2",
} as const

export type AuthLevel = (typeof AuthLevel)[keyof typeof AuthLevel]

export async function getSessionUser(): Promise<User | null> {
	await initPromise
	const {
		data: { session },
	} = await supabase.auth.getSession()
	return session?.user ?? null
}

export function isAllowedEmail(email: string): boolean {
	return email.toLowerCase().endsWith(`@${ALLOWED_EMAIL_DOMAIN}`)
}

export async function signOut() {
	return supabase.auth.signOut()
}

export async function forceSignOut() {
	await signOut()
	window.location.href = "/login"
}

export async function updatePassword(newPassword: string) {
	return supabase.auth.updateUser({ password: newPassword })
}

export async function signInWithPassword(email: string, password: string) {
	if (!isAllowedEmail(email)) {
		throw new Error(`Only @${ALLOWED_EMAIL_DOMAIN} emails are allowed`)
	}
	const { error } = await supabase.auth.signInWithPassword({ email, password })
	if (error) throw error
}

export async function getAuthLevel(): Promise<AuthLevel> {
	const { data } = await supabase.auth.mfa.getAuthenticatorAssuranceLevel()
	return (data?.currentLevel ?? AuthLevel.PasswordOnly) as AuthLevel
}

const SESSION_REVOKED_CODES = new Set([
	"bad_jwt",
	"session_not_found",
	"session_expired",
	"user_not_found",
	"user_banned",
])

// Fail-open: only an explicit revocation code invalidates the session —
// Supabase docs say to identify auth errors by code, not HTTP status.
// Network failures and other errors keep the session alive.
export async function isSessionStillValid(): Promise<boolean> {
	const result = await supabase.auth.getUser().catch(() => null)
	return !SESSION_REVOKED_CODES.has(result?.error?.code ?? "")
}

export async function listMfaFactors(): Promise<Factor[]> {
	const { data, error } = await supabase.auth.mfa.listFactors()
	if (error) throw error
	return data?.totp ?? []
}

export async function enrollTotpFactor() {
	return supabase.auth.mfa.enroll({
		factorType: "totp",
		friendlyName: `totp-${Date.now()}`,
		issuer: "Hecla Admin",
	})
}

export async function createMfaChallenge(factorId: string) {
	return supabase.auth.mfa.challenge({ factorId })
}

export async function verifyMfaChallenge(
	factorId: string,
	challengeId: string,
	code: string,
) {
	return supabase.auth.mfa.verify({ factorId, challengeId, code })
}

export interface MfaCheckResult {
	needsVerification: boolean
	needsEnrollment: boolean
	factor: Factor | null
}

export async function checkMfaRequired(): Promise<MfaCheckResult> {
	const factors = await listMfaFactors()
	const verifiedFactor = factors.find((f) => f.status === "verified")

	if (verifiedFactor) {
		return {
			needsVerification: true,
			needsEnrollment: false,
			factor: verifiedFactor,
		}
	}

	return { needsVerification: false, needsEnrollment: true, factor: null }
}

export interface MfaVerificationData {
	factorId: string
	challengeId: string
}

export async function prepareMfaVerification(
	factor: Factor,
): Promise<MfaVerificationData> {
	const { data, error } = await createMfaChallenge(factor.id)
	if (error) throw error
	return { factorId: factor.id, challengeId: data.id }
}

export interface MfaEnrollmentData {
	qrCode: string
	secret: string
	factorId: string
}

export async function prepareMfaEnrollment(): Promise<MfaEnrollmentData> {
	const factors = await listMfaFactors()
	const unverifiedFactors = factors.filter((f) => f.status === "unverified")

	for (const factor of unverifiedFactors) {
		await supabase.auth.mfa.unenroll({ factorId: factor.id })
	}

	const { data, error } = await enrollTotpFactor()
	if (error) throw error
	return {
		qrCode: data.totp.qr_code,
		secret: data.totp.secret,
		factorId: data.id,
	}
}

export async function verifyMfa(
	factorId: string,
	challengeId: string,
	code: string,
) {
	const { error } = await verifyMfaChallenge(factorId, challengeId, code)
	if (error) throw error
}

export async function enrollAndVerifyMfa(factorId: string, code: string) {
	const { data, error } = await createMfaChallenge(factorId)
	if (error) throw error
	await verifyMfa(factorId, data.id, code)
}

export async function getFreshAccessToken(): Promise<string | null> {
	const { data } = await supabase.auth.getSession()
	return data.session?.access_token ?? null
}

let refreshPromise: Promise<boolean> | null = null

export function refreshSessionOnce(): Promise<boolean> {
	if (!refreshPromise) {
		refreshPromise = supabase.auth
			.refreshSession()
			.then(({ error }) => !error)
			.catch(() => false)
			.finally(() => {
				refreshPromise = null
			})
	}
	return refreshPromise
}
